import type { Priority, TaskUrgency } from "@handoverpro/shared";

import {
  acknowledgePatient as sdkAcknowledgePatient,
  addTask as sdkAddTask,
  ApiLockedError,
  changePriority as sdkChangePriority,
  completePatient as sdkCompletePatient,
  completeTask as sdkCompleteTask,
  createPatient as sdkCreatePatient,
  escalatePatient as sdkEscalatePatient,
  escalateTask as sdkEscalateTask,
  getCurrentUser as sdkGetCurrentUser,
  getPatient as sdkGetPatient,
  getSessionTeam as sdkGetSessionTeam,
  listPatients as sdkListPatients,
  reopenPatient as sdkReopenPatient,
  reopenTask as sdkReopenTask,
  updatePatient as sdkUpdatePatient,
  type AcknowledgePatientResponseDto,
  type CreatePatientDto,
  type EscalatePatientResponseDto,
  type EscalateTaskResponseDto,
  type PatientResponseDto,
  type TeamMemberDto,
} from "~/lib/api";
import type { PatientFormValues } from "~/session/patient-form/schema";

/**
 * Network + mapping layer for the patient form (E4). Keeps the generated SDK and
 * the form-values ↔ wire-payload translation in one place so the routes and the
 * `<PatientForm>` stay free of API shape details. The SDK functions are aliased
 * to avoid colliding with the wrappers exported here.
 */

/** Form values → the create/update request body. */
export function toPatientPayload(values: PatientFormValues): CreatePatientDto {
  const alert = values.alert.trim();
  return {
    hospitalNumber: values.hospitalNumber,
    fullName: values.fullName,
    dateOfBirth: values.dateOfBirth,
    bedNumber: values.bedNumber,
    diagnosis: values.diagnosis,
    priority: values.priority,
    assignedDoctorId: values.assignedDoctorId === "" ? undefined : values.assignedDoctorId,
    alert: alert === "" ? undefined : alert,
    sbar: values.sbar,
    tasks: values.tasks,
  };
}

/**
 * A patient response → form values, for hydrating the edit form. Because PATCH
 * replaces the patient wholesale, the SBAR/tasks/priority/alert fields the E4.1
 * scaffold doesn't yet edit are carried through here so they round-trip
 * unchanged on save.
 */
export function toFormValues(patient: PatientResponseDto): PatientFormValues {
  return {
    hospitalNumber: patient.hospitalNumber,
    fullName: patient.fullName,
    dateOfBirth: patient.dateOfBirth,
    bedNumber: patient.bedNumber,
    diagnosis: patient.diagnosis,
    assignedDoctorId: patient.assignedDoctorId ?? "",
    priority: patient.priority,
    sbar: {
      situation: patient.sbar.situation,
      background: patient.sbar.background,
      assessment: patient.sbar.assessment,
      recommendation: patient.sbar.recommendation,
    },
    tasks: patient.tasks.map((task) => ({
      description: task.description,
      urgency: task.urgency,
    })),
    alert: patient.alert ?? "",
  };
}

export type FetchResult<T> =
  | { status: "ok"; data: T }
  | { status: "not-found" }
  | { status: "error"; message: string };

export type SaveResult =
  | { status: "ok"; patient: PatientResponseDto }
  | { status: "error"; message: string };

const GENERIC_SAVE_ERROR = "Something went wrong saving the patient. Please try again.";

/** Doctors on the session's ward, for the "assign to" dropdown. */
export async function fetchSessionTeam(sessionId: string): Promise<FetchResult<TeamMemberDto[]>> {
  const result = await sdkGetSessionTeam({ path: { sessionId } });
  if (result.response.status === 404) return { status: "not-found" };
  if (result.error || !result.data) {
    return { status: "error", message: `Couldn't load the ward team (${result.response.status}).` };
  }
  return { status: "ok", data: result.data };
}

/** Every patient on the session, each with its tasks (E4.6). 404 → not-found. */
export async function fetchSessionPatients(
  sessionId: string,
  archived?: boolean,
): Promise<FetchResult<PatientResponseDto[]>> {
  const result = await sdkListPatients({
    path: { sessionId },
    ...(archived ? { query: { archived: true } } : {}),
  });
  if (result.response.status === 404) return { status: "not-found" };
  if (result.error || !result.data) {
    return {
      status: "error",
      message: `Couldn't load the patient list (${result.response.status}).`,
    };
  }
  return { status: "ok", data: result.data };
}

/** A single patient, for hydrating the edit form. 404 → caller renders not-found. */
export async function fetchPatient(
  sessionId: string,
  patientId: string,
): Promise<FetchResult<PatientResponseDto>> {
  const result = await sdkGetPatient({ path: { sessionId, patientId } });
  if (result.response.status === 404) return { status: "not-found" };
  if (result.error || !result.data) {
    return { status: "error", message: `Couldn't load the patient (${result.response.status}).` };
  }
  return { status: "ok", data: result.data };
}

/** The signed-in user's application DB id (for the outgoing-doctor check). */
export async function fetchCurrentUserId(): Promise<string | null> {
  const result = await sdkGetCurrentUser();
  return result.data?.id ?? null;
}

function messageForSaveStatus(status: number): string {
  switch (status) {
    case 400:
      return "Some details are missing or invalid. Check the form and try again.";
    case 403:
      return "Only the doctor who started this handover can add or edit patients.";
    case 404:
      return "This session no longer exists, or you no longer have access to it.";
    case 423:
      return "This patient is locked and can no longer be edited.";
    default:
      return GENERIC_SAVE_ERROR;
  }
}

export async function createPatient(
  sessionId: string,
  values: PatientFormValues,
): Promise<SaveResult> {
  try {
    const result = await sdkCreatePatient({
      path: { sessionId },
      body: toPatientPayload(values),
    });
    if (result.error || !result.data) {
      return { status: "error", message: messageForSaveStatus(result.response.status) };
    }
    return { status: "ok", patient: result.data };
  } catch (err) {
    if (err instanceof ApiLockedError) {
      return { status: "error", message: messageForSaveStatus(423) };
    }
    return { status: "error", message: GENERIC_SAVE_ERROR };
  }
}

export async function updatePatient(
  sessionId: string,
  patientId: string,
  values: PatientFormValues,
): Promise<SaveResult> {
  try {
    const result = await sdkUpdatePatient({
      path: { sessionId, patientId },
      body: toPatientPayload(values),
    });
    if (result.error || !result.data) {
      return { status: "error", message: messageForSaveStatus(result.response.status) };
    }
    return { status: "ok", patient: result.data };
  } catch (err) {
    if (err instanceof ApiLockedError) {
      return { status: "error", message: messageForSaveStatus(423) };
    }
    return { status: "error", message: GENERIC_SAVE_ERROR };
  }
}

/**
 * The result of acknowledging a patient (HAN-56). Each variant maps to a
 * distinct UX: success → close modal + toast (+ navigate if the session locked
 * because this was the final patient); `session_locked` and `already_acked`
 * are stale-tab cases the user can't retry through, so the caller refreshes
 * rather than keeping the modal open; `not_incoming` is a permission edge case
 * (probably stale page after a code re-validation), and `error` is the catch-
 * all that should leave the modal open for a manual retry.
 */
export type AcknowledgeResult =
  | { status: "ok"; data: AcknowledgePatientResponseDto }
  | { status: "session_locked" }
  | { status: "already_acked" }
  | { status: "not_incoming" }
  | { status: "not_found" }
  | { status: "error" };

/**
 * Record the incoming doctor's sign-off on a patient
 * (`POST /sessions/:sessionId/patients/:patientId/acknowledge`). The interceptor
 * (`~/lib/api.ts`) throws ApiLockedError for 423; we catch and map it. 409 and
 * 403 come back through the normal `result.error` channel.
 */
export async function acknowledgePatient(
  sessionId: string,
  patientId: string,
): Promise<AcknowledgeResult> {
  try {
    const result = await sdkAcknowledgePatient({ path: { sessionId, patientId } });
    if (result.data && !result.error) {
      return { status: "ok", data: result.data };
    }
    switch (result.response.status) {
      case 409:
        return { status: "already_acked" };
      case 403:
        return { status: "not_incoming" };
      case 404:
        return { status: "not_found" };
      default:
        return { status: "error" };
    }
  } catch (err) {
    if (err instanceof ApiLockedError) return { status: "session_locked" };
    return { status: "error" };
  }
}

/**
 * The result of escalating a task (HAN-79). `ok` → toast naming who was notified
 * + refresh; `already_escalated` (409) is a stale-tab/double-click the user
 * can't retry through, so the caller refreshes; `session_locked` (423) and
 * `not_found` navigate away; `error` is the catch-all that keeps the dialog open
 * for a manual retry.
 */
export type EscalateResult =
  | { status: "ok"; data: EscalateTaskResponseDto }
  | { status: "already_escalated" }
  | { status: "not_found" }
  | { status: "session_locked" }
  | { status: "error" };

/**
 * Escalate a task to the senior clinician(s) on the ward
 * (`POST …/tasks/:taskId/escalate`). 409 → already escalated; 423 (locked)
 * arrives via the ApiLockedError interceptor.
 */
export async function escalateTask(
  sessionId: string,
  patientId: string,
  taskId: string,
  reason?: string,
): Promise<EscalateResult> {
  try {
    const result = await sdkEscalateTask({
      path: { sessionId, patientId, taskId },
      body: reason && reason.trim() ? { reason: reason.trim() } : {},
    });
    if (result.data && !result.error) return { status: "ok", data: result.data };
    switch (result.response.status) {
      case 409:
        return { status: "already_escalated" };
      case 404:
        return { status: "not_found" };
      default:
        return { status: "error" };
    }
  } catch (err) {
    if (err instanceof ApiLockedError) return { status: "session_locked" };
    return { status: "error" };
  }
}

/**
 * The result of escalating a whole patient (HAN-80). Mirrors {@link EscalateResult}:
 * `ok` → toast naming who was notified + refresh; `already_escalated` (409) is a
 * stale-tab/double-click the caller refreshes through; `not_found` navigates
 * away; `error` keeps the dialog open for a manual retry.
 */
export type EscalatePatientResult =
  | { status: "ok"; data: EscalatePatientResponseDto }
  | { status: "already_escalated" }
  | { status: "not_found" }
  | { status: "error" };

/**
 * Escalate a whole patient to the senior clinician(s) on the ward
 * (`POST …/patients/:patientId/escalate`). 409 → already escalated. There is no
 * locked-session path here — whole-patient escalation is an emergency action the
 * API allows regardless of session state (the frontend hides the affordance once
 * the patient is acknowledged).
 */
export async function escalatePatient(
  sessionId: string,
  patientId: string,
  reason?: string,
): Promise<EscalatePatientResult> {
  try {
    const result = await sdkEscalatePatient({
      path: { sessionId, patientId },
      body: reason && reason.trim() ? { reason: reason.trim() } : {},
    });
    if (result.data && !result.error) return { status: "ok", data: result.data };
    switch (result.response.status) {
      case 409:
        return { status: "already_escalated" };
      case 404:
        return { status: "not_found" };
      default:
        return { status: "error" };
    }
  } catch {
    return { status: "error" };
  }
}

// ---------------------------------------------------------------------------
// Patient action wrappers (task management, priority, complete/reopen)
// ---------------------------------------------------------------------------

/**
 * Shared result type for all single-patient mutation actions. `ok` carries the
 * refreshed `PatientResponseDto` returned by the backend; `conflict` (409) means
 * the action would produce an invalid state (e.g. completing an already-completed
 * task); `not_found` (404) means the patient or task has been removed; `error`
 * is the catch-all with a user-readable message.
 */
export type PatientActionResult =
  | { status: "ok"; patient: PatientResponseDto }
  | { status: "conflict" }
  | { status: "not_found" }
  | { status: "error"; message: string };

const GENERIC_ACTION_ERROR = "Something went wrong. Please try again.";

function mapAction(result: {
  data?: PatientResponseDto;
  error?: unknown;
  response: { status: number };
}): PatientActionResult {
  if (result.data && !result.error) return { status: "ok", patient: result.data };
  switch (result.response.status) {
    case 409:
      return { status: "conflict" };
    case 404:
      return { status: "not_found" };
    case 400:
      return { status: "error", message: "A high-priority patient needs at least one task." };
    default:
      return { status: "error", message: GENERIC_ACTION_ERROR };
  }
}

/** Add a new task to a patient (`POST …/tasks`). Returns the refreshed patient. */
export async function addTask(
  sessionId: string,
  patientId: string,
  description: string,
  urgency: TaskUrgency,
): Promise<PatientActionResult> {
  return mapAction(
    await sdkAddTask({ path: { sessionId, patientId }, body: { description, urgency } }),
  );
}

/** Mark a task done (`POST …/tasks/:taskId/complete`). Returns the refreshed patient. */
export async function completeTask(
  sessionId: string,
  patientId: string,
  taskId: string,
): Promise<PatientActionResult> {
  return mapAction(await sdkCompleteTask({ path: { sessionId, patientId, taskId } }));
}

/** Re-open a completed task (`POST …/tasks/:taskId/reopen`). Returns the refreshed patient. */
export async function reopenTask(
  sessionId: string,
  patientId: string,
  taskId: string,
): Promise<PatientActionResult> {
  return mapAction(await sdkReopenTask({ path: { sessionId, patientId, taskId } }));
}

/** Change a patient's triage priority (`PATCH …/priority`). Returns the refreshed patient. */
export async function changePriority(
  sessionId: string,
  patientId: string,
  priority: Priority,
): Promise<PatientActionResult> {
  return mapAction(await sdkChangePriority({ path: { sessionId, patientId }, body: { priority } }));
}

/** Mark a patient complete / archived (`POST …/complete`). Returns the refreshed patient. */
export async function completePatient(
  sessionId: string,
  patientId: string,
): Promise<PatientActionResult> {
  return mapAction(await sdkCompletePatient({ path: { sessionId, patientId } }));
}

/** Re-open a completed patient (`POST …/reopen`). Returns the refreshed patient. */
export async function reopenPatient(
  sessionId: string,
  patientId: string,
): Promise<PatientActionResult> {
  return mapAction(await sdkReopenPatient({ path: { sessionId, patientId } }));
}
